/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc. *
 *
 * @author VGUNAD4
 * 01/12/2025
 *
 */

import React, { useState, useEffect } from 'react';
import Table from "../../components/generic/table/table";
import tableConfigs from "../../components/generic/table/tableConfigs";
import EventEmitter from "../../eventEmmiters/EventEmitter";
import { useHistory } from 'react-router-dom';
import axios from 'axios';

const table = tableConfigs.cacheReset;

const CacheReset = () => {
    const [cacheEntries, setCacheEntries] = useState([]);
    const [host, setHost] = useState('');
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [status, setStatus] = useState({});
    const history = useHistory();


    useEffect(() => {
        EventEmitter.emit('setPageHeaderData', {
            page: 'cache-reset',
            pageName: "Cache Reset",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/cache-reset",
            optionalIcons: ['manageColumnsIcon']
        });
        fetchCacheEntries();
    }, []);

    const fetchCacheEntries = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('tmcUser'));
            const token = 'Bearer ' + user.token;

            // set the dynamic base URL
            const baseUrl = process.env.REACT_APP_BASE_URL ;
            const apiUrl = `${baseUrl}/cache-setup?page-no=0`;

            const response = await axios.get(apiUrl, {
                headers: { Authorization: token }
            });

            if (response.data && Array.isArray(response.data)) {
                // Assume response includes statuses for each entry
                const entries = response.data;
                const updatedStatus = {};
                entries.forEach(entry => {
                    updatedStatus[entry.host] = entry.status || 'pending';
                });
                setStatus(updatedStatus);
                setCacheEntries(entries);
            } else {
                console.error("Unexpected response format:", response.data);
            }
        } catch (error) {
            console.error("Error fetching cache setup:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCacheReset = async () => {
        setLoading(true);

        try {
            const user = JSON.parse(localStorage.getItem('tmcUser'));
            if (!user || !user.mail) {
                throw new Error("User information is missing.");
            }

            const token = `Bearer ${user.token}`;
            console.log('Token:', token);

            // set the dynamic base URL
            const baseUrl = process.env.REACT_APP_BASE_URL ;
            const apiUrl = `${baseUrl}/cache-setup/resetCache`;

            const response = await axios.post(
                apiUrl,
                { email: user.mail },
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );

            if (Array.isArray(response.data)) {
                setCacheEntries(response.data); // Assuming backend returns the updated cache entries
                setSuccessMessage('Cache reset completed successfully.');
            } else {
                setError('Unexpected response format.');
            }
        } catch (error) {
            console.error("Error resetting cache:", error);
        } finally {
            setLoading(false);
        }
    };


    // const handleAddCacheReset = async () => {
    //     if (!host || !url) {
    //         setError("Both Host and URL are required.");
    //         return;
    //     }
    //     setLoading(true);
    //     setError('');
    //     setSuccessMessage('');
    //     try {
    //         const response = await axios.post('/api/cache-reset', { host, url });
    //         setSuccessMessage("Cache reset added successfully.");
    //         setCacheEntries([...cacheEntries, response.data]);
    //         setHost('');
    //         setUrl('');
    //     } catch (err) {
    //         setError("Error adding cache reset.");
    //     }
    //     setLoading(false);
    // };
    //
    // const handleRemoveCacheReset = async (id) => {
    //     setLoading(true);
    //     try {
    //         await axios.delete(`/api/cache-reset/${id}`);
    //         setCacheEntries(cacheEntries.filter(entry => entry.id !== id));
    //     } catch (err) {
    //         setError("Error removing cache reset.");
    //     }
    //     setLoading(false);
    // };

    return (
        <div className='page-mainLayout'>

            <h2 className="cache-reset-title">Cache Reset</h2>
            <table className='cache-reset-table'>
                <thead>
                <tr>
                    <th>Host</th>
                    <th>URL</th>
                    <th>Status</th>
                </tr>
                </thead>
                <tbody>
                {cacheEntries.map(entry => (
                    <tr key={entry.host}>
                        <td>{entry.host}</td>
                        <td>{entry.url}</td>
                        <td>
                            {entry.status === 'Success' && <span className="success">Successful</span>}
                            {entry.status === 'Failure' && <span className="error">Unsuccessful</span>}
                            {!entry.status && <span className="pending">Pending</span>}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <button className="reset-btn" onClick={handleCacheReset} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Cache'}
            </button>

            <Table tableConfig={table} data={cacheEntries} />

        </div>
    );
};



export default CacheReset;
